// Core packages

// Third party packages

// Custom packages

/**
 * Pure helpers of "Scarica tutti" in the download dialogs of the two archive pages: one zip with
 * every file of an archive row, built by the back-end.
 *
 * - documents page (legacy archive):
 *   `GET /api/v2/document-archives/:documentId/docs/:subDocId/files/zip`
 * - new archive page (`documentArchiveDocs`): `GET /api/v2/document-archive-docs/:docId/files/zip`
 *
 * Both endpoints answer with the same headers, statuses and naming rules, so every rule below
 * serves both dialogs: only what the button needs in order to be offered differs.
 *
 * The back-end names the zip and the files inside it, and tells in a header how many files of the
 * row it could not read. What is left to the front-end is deciding when to offer the button,
 * reading those two headers, and picking a translated text for each failure: the back-end
 * messages are technical and in English.
 *
 * @since 3.0.0
 */

/** Fewer files than this and the dialog does not offer the zip: a single file downloads as it is */
export const MIN_FILES_FOR_ZIP = 2;

/** Response header with the number of files of the row left out of the zip */
export const SKIPPED_FILES_HEADER = "X-Archive-Skipped-Files";

/** Longest barcode part of the zip name, as the back-end cuts it */
const MAX_ZIP_BASE_NAME_LENGTH = 100;

/** Zip name the back-end gives a row without barcode */
const ZIP_BASE_NAME_WITHOUT_BARCODE = "documento";

/** Prefix of the i18n keys of the zip download */
const I18N_PREFIX = "properties.documents.downloadDialog.zip";

/** `filename*=` (RFC 5987, UTF-8 percent-encoded): read before the plain form */
const EXTENDED_FILE_NAME = /filename\*\s*=\s*([^;]+)/i;

/** `filename=`, quoted or bare */
const PLAIN_FILE_NAME = /filename\s*=\s*("[^"]*"|[^;]+)/i;

/** The part of the dialog data that decides whether the zip can be offered */
export interface DocFilesZipSource {
  /** Archive of the property: `documentId` of the endpoint */
  documentArchiveId?: unknown;
  /** `docId` of the page row: `subDocId` of the endpoint */
  docId?: unknown;
  /** Entries of `data.document[]` of the row */
  documents?: unknown;
}

/** An i18n key with the values its text interpolates */
export interface ZipDownloadMessage {
  key: string;
  params?: { [name: string]: number };
}

function isFilledString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

/**
 * True when the dialog of the documents page can offer "Scarica tutti": the row has at least two
 * files and the page passed the two ids the endpoint is addressed by.
 *
 * The ids are what keeps the button on the documents page. The same dialog opens from many other
 * pages (companies, suppliers, deadlines, searches, the public QR code page without login) that
 * pass the files alone: there the button stays hidden and the dialog is unchanged.
 *
 * @param data data the dialog was opened with
 */
export function canDownloadDocFilesZip(
  data: DocFilesZipSource | null | undefined
): boolean {
  return (
    !!data &&
    isFilledString(data.documentArchiveId) &&
    isFilledString(data.docId) &&
    Array.isArray(data.documents) &&
    data.documents.length >= MIN_FILES_FOR_ZIP
  );
}

/**
 * True when the dialog of the new archive can offer "Scarica tutti": a document id to address the
 * endpoint by, and at least two files in the list the dialog shows. That list holds the entries
 * with a `filename`, the same ones the back-end puts in the zip.
 *
 * @param docId `_id` of the `documentArchiveDocs` row
 * @param fileCount number of files the dialog lists
 */
export function canDownloadArchiveDocFilesZip(
  docId: unknown,
  fileCount: number
): boolean {
  return isFilledString(docId) && fileCount >= MIN_FILES_FOR_ZIP;
}

/**
 * Zip name to use when the response carries no readable `Content-Disposition`. Same rule as the
 * back-end: the barcode without surrounding spaces, every character other than A-Z a-z 0-9 - _
 * replaced by _, at most 100 characters; "documento" when there is no barcode.
 *
 * @param barCode `data.barCode` of the row
 */
export function fallbackZipFileName(barCode: unknown): string {
  const base =
    typeof barCode === "string"
      ? barCode
          .trim()
          .replace(/[^A-Za-z0-9_-]/g, "_")
          .slice(0, MAX_ZIP_BASE_NAME_LENGTH)
      : "";
  return `${base !== "" ? base : ZIP_BASE_NAME_WITHOUT_BARCODE}.zip`;
}

function stripQuotes(value: string): string {
  return value.length >= 2 &&
    value.charAt(0) === '"' &&
    value.charAt(value.length - 1) === '"'
    ? value.slice(1, -1)
    : value;
}

/**
 * Drops control characters and turns path separators into "_". The name comes from a response
 * header and only names the saved file: it must not be able to address a folder.
 */
function sanitizeFileName(name: string): string | null {
  const cleaned = name
    .split("")
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 0x1f && code !== 0x7f;
    })
    .join("")
    .replace(/[\\/]/g, "_")
    .trim();
  return cleaned !== "" ? cleaned : null;
}

/** Value of `filename*=`, decoded. Null when it is missing, not UTF-8 or badly encoded */
function decodeExtendedFileName(header: string): string | null {
  const match = EXTENDED_FILE_NAME.exec(header);
  if (!match) {
    return null;
  }
  // Shape: charset'language'percent-encoded-value, with the language usually empty
  const value = stripQuotes(match[1].trim());
  const firstQuote = value.indexOf("'");
  const secondQuote = firstQuote >= 0 ? value.indexOf("'", firstQuote + 1) : -1;
  if (
    firstQuote < 0 ||
    secondQuote < 0 ||
    value.slice(0, firstQuote).toLowerCase() !== "utf-8"
  ) {
    return null;
  }
  try {
    return decodeURIComponent(value.slice(secondQuote + 1));
  } catch {
    // A malformed escape sequence makes decodeURIComponent throw: the plain form is read instead
    return null;
  }
}

/**
 * Name of the saved zip as the back-end sends it in `Content-Disposition`: the UTF-8 `filename*=`
 * first, then the plain `filename=`. Null when the header is missing or names nothing, and the
 * caller then falls back to `fallbackZipFileName`.
 *
 * @param header value of the `Content-Disposition` response header
 */
export function zipFileNameFromContentDisposition(
  header: string | null | undefined
): string | null {
  if (typeof header !== "string" || header.trim() === "") {
    return null;
  }
  const extended = decodeExtendedFileName(header);
  const extendedName = extended !== null ? sanitizeFileName(extended) : null;
  if (extendedName !== null) {
    return extendedName;
  }
  const plain = PLAIN_FILE_NAME.exec(header);
  return plain ? sanitizeFileName(stripQuotes(plain[1].trim())) : null;
}

/**
 * Number of files left out of the zip, from `X-Archive-Skipped-Files`. 0 when the header is
 * missing or is not a whole number: the warning shows only on a value the back-end really sent.
 *
 * @param header value of the response header
 */
export function skippedFilesCount(header: string | null | undefined): number {
  if (typeof header !== "string" || !/^\s*\d+\s*$/.test(header)) {
    return 0;
  }
  return parseInt(header, 10);
}

/**
 * Warning about the files left out of the zip, or null when every file is in it.
 *
 * @param count number of files left out
 */
export function skippedFilesMessage(count: number): ZipDownloadMessage | null {
  if (!(count > 0)) {
    return null;
  }
  return count === 1
    ? { key: `${I18N_PREFIX}.skippedOne` }
    : { key: `${I18N_PREFIX}.skippedMany`, params: { count } };
}

/**
 * Minutes to wait before asking again, from `Retry-After` (delta-seconds or an HTTP date).
 * Rounded up and never below 1. Null when the header is missing or unreadable.
 *
 * @param header value of the `Retry-After` response header
 * @param nowMs current time, in milliseconds
 */
export function retryAfterMinutes(
  header: string | null | undefined,
  nowMs: number
): number | null {
  if (typeof header !== "string" || header.trim() === "") {
    return null;
  }
  const value = header.trim();
  let seconds: number;
  if (/^\d+$/.test(value)) {
    seconds = parseInt(value, 10);
  } else {
    const retryAt = Date.parse(value);
    if (isNaN(retryAt)) {
      return null;
    }
    seconds = (retryAt - nowMs) / 1000;
  }
  return Math.max(1, Math.ceil(seconds / 60));
}

/**
 * Translated text for a failed zip download, picked by HTTP status. 0 stands for a failure
 * without status: network down, or the connection cut once the zip had started.
 *
 * The 403 text asks to download the files one at a time on purpose: 403 is also the answer for
 * the properties whose company data are misaligned, where the single download still works.
 *
 * @param status HTTP status of the failed response, 0 when there is none
 * @param retryAfterHeader `Retry-After` of a 429 response
 * @param nowMs current time, in milliseconds
 */
export function zipDownloadErrorMessage(
  status: number,
  retryAfterHeader: string | null | undefined,
  nowMs: number
): ZipDownloadMessage {
  switch (status) {
    case 401:
      return { key: `${I18N_PREFIX}.errors.sessionExpired` };
    case 403:
      return { key: `${I18N_PREFIX}.errors.forbidden` };
    case 404:
      return { key: `${I18N_PREFIX}.errors.notFound` };
    case 410:
      return { key: `${I18N_PREFIX}.errors.gone` };
    case 429: {
      const minutes = retryAfterMinutes(retryAfterHeader, nowMs);
      if (minutes === null) {
        return { key: `${I18N_PREFIX}.errors.tooManyRequests` };
      }
      return minutes === 1
        ? { key: `${I18N_PREFIX}.errors.tooManyRequestsOne` }
        : { key: `${I18N_PREFIX}.errors.tooManyRequestsMany`, params: { minutes } };
    }
    default:
      return { key: `${I18N_PREFIX}.errors.generic` };
  }
}
